import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Key, ExternalLink, ArrowRight, CheckCircle2, Info } from "lucide-react";
import { BYOK_PROVIDERS } from "@/data/byokProviders";

/**
 * BYOK Explainer — landing-page section that answers the founder's
 * top question for new Premium users: "what keys do I even need?"
 *
 * Pitched as: bring your own keys, use one creative workspace, pay
 * providers directly for usage. Lists each supported provider with
 * what it unlocks and where to get a key.
 */
export default function ByokExplainer() {
  return (
    <section id="byok-explainer" data-testid="byok-explainer" className="relative py-24 md:py-32 px-6 md:px-12 luxe-bg overflow-hidden">
      {/* Gold vignette */}
      <div className="absolute -right-32 top-0 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[140px]" aria-hidden="true" />
      <div className="absolute -left-32 bottom-0 w-[420px] h-[420px] rounded-full bg-[#D4AF37]/8 blur-[120px]" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto">
        {/* Eyebrow + heading */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-14 max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[11px] md:text-xs font-bold uppercase tracking-[0.32em] text-[#D4AF37]">
              Premium · Bring Your Own Keys
            </span>
          </div>
          <h2 data-testid="byok-explainer-heading" className="font-display text-4xl md:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tighter">
            Bring your own keys.<br />
            <span className="gold-shimmer">One creative workspace.</span>
          </h2>
          <p className="text-[#A0A0A0] text-base md:text-lg mt-6 leading-relaxed max-w-2xl">
            Stop stitching tools together. Use our ready-made platform as your creation interface,
            connect your own provider keys, and pay OpenAI / Gemini / Anthropic / ElevenLabs / fal.ai
            directly for usage. <span className="text-[#F4F4F0]">$100/month gets you the workspace; the providers bill you for what you use.</span>
          </p>
        </motion.div>

        {/* What-keys-do-I-need grid */}
        <p data-testid="byok-section-eyebrow" className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-5">
          What keys do I need?
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {BYOK_PROVIDERS.map((p, idx) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                data-testid={`byok-explainer-card-${p.id}`}
                className={`relative border border-[#2A2A2A] bg-gradient-to-br ${p.color} to-[#0A0A0A] p-6 gold-glow group`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`inline-flex items-center justify-center w-10 h-10 border border-[#D4AF37]/30 bg-[#080808] ${p.accent}`}>
                    <Icon className="w-5 h-5" strokeWidth={1.7} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl font-bold text-[#F4F4F0]">{p.name}</h3>
                    <p className="text-xs text-[#A0A0A0] mt-1">{p.summary}</p>
                  </div>
                </div>

                <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] mb-2 font-semibold">What this unlocks</p>
                <ul className="space-y-1.5 mb-4">
                  {p.unlocks.map((u, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-[#C8C8C8] leading-relaxed">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#D4AF37] shrink-0 mt-0.5" />
                      <span>{u}</span>
                    </li>
                  ))}
                </ul>

                {p.note && (
                  <p className="text-[11px] text-[#7A7A7A] italic leading-relaxed mb-4 border-l-2 border-[#D4AF37]/30 pl-3">
                    {p.note}
                  </p>
                )}

                <a
                  href={p.where}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`byok-explainer-where-${p.id}`}
                  className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider ${p.accent} hover:opacity-80 transition-opacity`}
                >
                  Get your {p.name} key <ExternalLink className="w-3 h-3" />
                </a>
              </motion.div>
            );
          })}
        </div>

        {/* Sora transparency callout — honest, not buried */}
        <div className="flex items-start gap-3 p-4 border border-amber-500/30 bg-amber-500/5 text-amber-200 mb-12 max-w-4xl" data-testid="byok-sora-callout-landing">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="text-xs md:text-sm leading-relaxed">
            <span className="font-semibold">Honest note on Sora 2 video:</span> Even with your OpenAI key connected,
            Sora 2 video calls currently proxy through Emergent's gateway because OpenAI hasn't opened public Sora API
            access yet. Image, voice, ad scripts and writing all use <span className="text-amber-100">your keys directly</span>{" "}
            today. The day OpenAI opens partner Sora access, video flips onto your key automatically.
          </div>
        </div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="border border-[#D4AF37]/40 bg-gradient-to-br from-[#121212] to-[#080808] p-8 md:p-10 sparkle-bed"
        >
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div className="flex-1 max-w-2xl">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-3">Premium · $100 / month</p>
              <h3 className="font-display text-3xl md:text-4xl font-black tracking-tighter mb-3">
                Use your own keys on our fully-built creative platform.
              </h3>
              <p className="text-[#A0A0A0] text-sm md:text-base leading-relaxed">
                Premium unlocks the BYOK vault. Connect any provider, generate freely, pay them directly
                for your usage. We're the interface; you own the billing relationship.
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-[#C8C8C8]">
                <li className="flex items-center gap-2"><Key className="w-3.5 h-3.5 text-[#D4AF37]" /> Encrypted key vault</li>
                <li className="flex items-center gap-2"><Key className="w-3.5 h-3.5 text-[#D4AF37]" /> Per-call routing transparency</li>
                <li className="flex items-center gap-2"><Key className="w-3.5 h-3.5 text-[#D4AF37]" /> All generators, no watermark</li>
                <li className="flex items-center gap-2"><Key className="w-3.5 h-3.5 text-[#D4AF37]" /> White-label client renders</li>
              </ul>
            </div>
            <div className="flex flex-col gap-3 shrink-0">
              <Link
                to="/register"
                data-testid="byok-explainer-primary-cta"
                className="inline-flex items-center justify-center gap-2 gold-sheen font-bold px-7 py-4 transition-all shadow-[0_8px_32px_-8px_rgba(212,175,55,0.6)]"
              >
                Go Premium — Connect Your Keys
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/register"
                data-testid="byok-explainer-secondary-cta"
                className="inline-flex items-center justify-center gap-2 text-[#D4AF37] border border-[#D4AF37]/40 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 px-7 py-3.5 transition-colors text-sm"
              >
                Or start free with pay-as-you-go
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
